import React, { useMemo } from 'react';
import { ModalProvider } from '@/modules/Modal/context';
import { ModalHeader, ModalBody, ModalFooter } from '@/modules/Modal/components';
import type { ModalBadge, ModalButton, ModalIcon } from '@/shared/types/modal';
import { cn } from '@/shared/utils';
import { BUTTON_SLOT_SPREAD_CENTER } from '@/shared/types/button';

function applyFooterSlotSpread(
  button: ModalButton | undefined,
  enabled: boolean
): ModalButton | undefined {
  if (!button || !enabled) {
    return button;
  }
  return {
    ...button,
    slotLayout: BUTTON_SLOT_SPREAD_CENTER,
    fullWidth: true,
    className: cn('ui-flex-1 ui-min-w-0', button.className),
  };
}

export interface PopupStructuredLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  showHeader: boolean;
  showFooter: boolean;
  showCloseButton: boolean;
  title?: string;
  description?: string;
  iconConfig?: ModalIcon;
  badge?: ModalBadge;
  badges?: ModalBadge[];
  headerButton?: ModalButton;
  headerButtons?: ModalButton[];
  footerPrimaryButton?: ModalButton;
  footerSecondaryButton?: ModalButton;
  footerButtons?: ModalButton[];
  footerAuxButton?: ModalButton;
  /** `spread`: iconos a extremos y texto centrado en cada botón del footer (vía `InnerButton`). */
  footerSlotLayout?: 'inline' | 'spread';
  bodyWithPadding: boolean;
  children: React.ReactNode;
}

/**
 * Contenido estructurado tipo popover de Figma: mismas piezas visuales que el modal (Provider + Header/Body/Footer)
 * sin alterar el módulo Modal.
 */
export function PopupStructuredLayout({
  isOpen,
  onClose,
  showHeader,
  showFooter,
  showCloseButton,
  title,
  description,
  iconConfig,
  badge,
  badges,
  headerButton,
  headerButtons,
  footerPrimaryButton,
  footerSecondaryButton,
  footerButtons,
  footerAuxButton,
  footerSlotLayout = 'inline',
  bodyWithPadding,
  children,
}: PopupStructuredLayoutProps) {
  const spreadFooter = footerSlotLayout === 'spread';

  const { mergedPrimary, mergedSecondary, mergedButtons } = useMemo(() => {
    return {
      mergedPrimary: applyFooterSlotSpread(footerPrimaryButton, spreadFooter),
      mergedSecondary: applyFooterSlotSpread(footerSecondaryButton, spreadFooter),
      mergedButtons: footerButtons?.map((b) => applyFooterSlotSpread(b, spreadFooter) ?? b),
    };
  }, [footerPrimaryButton, footerSecondaryButton, footerButtons, spreadFooter]);

  const hasFooterContent =
    !!footerAuxButton ||
    (mergedButtons && mergedButtons.length > 0) ||
    !!mergedPrimary ||
    !!mergedSecondary;

  const showFooterBlock = showFooter && hasFooterContent;

  return (
    <ModalProvider
      isOpen={isOpen}
      onClose={onClose}
      sectionPadding="sm"
      showCancelButton={false}
      showSuccessButton={false}
      hideFooter={!showFooterBlock}
      showCloseButton={showCloseButton}
      iconConfig={iconConfig}
      badge={badge}
      badges={badges}
      headerButton={headerButton}
      headerButtons={headerButtons}
      footerAuxButton={footerAuxButton}
      footerPrimaryButton={mergedPrimary}
      footerSecondaryButton={mergedSecondary}
      footerButtons={mergedButtons}
    >
      {showHeader ? (
        <ModalHeader showCloseButton={showCloseButton}>
          {title || description ? (
            <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-2">
              {title ? (
                <span className="ui-text-heading-h6 ui-font-heading-h6 ui-leading-heading-h6 ui-tracking-heading-h6 ui-text-primary">
                  {title}
                </span>
              ) : null}
              {description ? (
                <p className="ui-line-clamp-2 ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-text-tertiary">
                  {description}
                </p>
              ) : null}
            </div>
          ) : null}
        </ModalHeader>
      ) : null}
      <ModalBody withPadding={bodyWithPadding}>{children}</ModalBody>
      {showFooterBlock ? <ModalFooter /> : null}
    </ModalProvider>
  );
}
